import cn from 'classnames'
import { isValidElement, memo, useEffect, useRef, useState } from 'react'

import { renderItemContent } from '../../helpers/renderItemContent'
import { InnerList } from '../innerList/InnerList'
import { ILabelProps } from '../label/models'

import { DEFAULT_POSITION } from './configs'
import { ISubListProps } from './models'

import styles from './SubList.module.scss'

export const SubList = memo<ISubListProps>(
  ({ className, children, isDisabled, isVisible = true, hasShadow }) => {
    if (Array.isArray(children) && !isValidElement<ILabelProps>(children[0])) {
      throw '`SubList` component should has `Label` as first child component'
    }

    if (!Array.isArray(children)) {
      throw '`SubList` component should has `Label` as first child component and one or some `Item` components'
    }

    const [listPosition, setListPosition] = useState(DEFAULT_POSITION)
    const [isSubListVisible, setIsSubListVisible] = useState(false)

    const innerRef = useRef<HTMLDivElement>(null)
    const subListRef = useRef<HTMLDivElement>(null)
    const labelComponent = children[0]
    const list = children.slice(1)
    const windowInnerWidth = window.innerWidth

    const subElementDimensions = innerRef.current?.getBoundingClientRect()
    const subListElementDimensions = subListRef.current?.getBoundingClientRect()
    const subElementWidth = subElementDimensions?.width
    const subElementRight = subElementDimensions?.right
    const subListElementWidth = subListElementDimensions?.width

    useEffect(() => {
      if (subElementRight && subElementWidth && subListElementWidth) {
        const offsetY = 0
        const offsetX =
          windowInnerWidth > subElementRight + subListElementWidth
            ? subElementWidth
            : -subElementWidth

        setListPosition({
          top: offsetY,
          left: offsetX,
        })
      }
    }, [subElementRight, subElementWidth, subListElementWidth, windowInnerWidth])

    useEffect(() => {
      const subElement = innerRef && innerRef.current

      if (subElement && !isDisabled) {
        subElement.onmouseenter = () => {
          setIsSubListVisible(true)
        }
        subElement.onmouseleave = () => {
          setIsSubListVisible(false)
        }
      }

      return () => {
        setIsSubListVisible(false)
      }
    }, [isDisabled])

    if (isVisible) {
      return (
        <div
          className={cn(styles.subList, { [styles.disabled]: isDisabled }, className)}
          ref={innerRef}
        >
          <div className={cn(styles.subList_label, { [styles.disabled]: isDisabled })}>
            {renderItemContent(labelComponent)}
            <i className="kbase kbase-omni-chevron-right" />
          </div>
          <div
            ref={subListRef}
            className={cn(styles.subList_list, { [styles.hidden]: !isSubListVisible })}
            style={listPosition}
          >
            <InnerList hasShadow={hasShadow}>{list}</InnerList>
          </div>
        </div>
      )
    }

    return null
  }
)
